import React, { useEffect, useRef } from "react";
import { PromptBox } from "../ui/chatgpt-prompt-input";
import { MessageBubble, StreamingCursor } from "./MessageBubble";
import { DozeLogo } from "./DozeLogo";
import { useChat } from "../../hooks/useChat";
import { ASSISTANT_NAME } from "../../config";

const SUGGESTIONS = [
  { label: "I sleep hot", prompt: "I'm a hot sleeper and wake up sweating. What do you recommend?" },
  { label: "Lower back pain", prompt: "I have lower back pain and sleep on my back. Best mattress?" },
  { label: "Best under $600", prompt: "What is your best mattress for under $600?" },
  { label: "Firm and soft couple", prompt: "My partner likes a firm bed and I like it soft. What works for us?" },
];

/**
 * Main chat shell.
 *
 * Claude-inspired warm aesthetic. Fills the section width and most of the
 * viewport height on desktop, full height on mobile.
 */
export function ChatShell() {
  const { messages, isStreaming, toolActivity, sendMessage, reset } = useChat();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Pin the message list to the bottom by scrolling ONLY the inner container,
  // never the host page (scrollIntoView would scroll the whole window).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isStreaming]);

  // Focus the input without scrolling the page to the widget.
  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const text = (data.get("message") as string) ?? "";
    if (!text.trim()) return;
    sendMessage(text);
    form.reset();
    const textarea = form.querySelector("textarea");
    if (textarea) {
      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype,
        "value",
      )?.set;
      nativeInputValueSetter?.call(textarea, "");
      textarea.dispatchEvent(new Event("input", { bubbles: true }));
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.currentTarget.form?.requestSubmit();
    }
  }

  const lastMessage = messages[messages.length - 1];
  const isLastMessageStreaming =
    isStreaming && lastMessage?.role === "assistant" && lastMessage.content.length > 0;
  // Show the cute "thinking" bubble while waiting for the first token
  const showThinking =
    isStreaming &&
    (!lastMessage || lastMessage.role !== "assistant" || lastMessage.content.length === 0);
  const isIntro = messages.length <= 1 && !isStreaming;

  return (
    <div
      className={[
        "doze-shell-in flex flex-col w-full overflow-hidden",
        // Mobile: tall, edge to edge
        "h-[86vh] rounded-2xl",
        // Desktop: fill the section and most of the screen
        "md:h-[82vh] md:rounded-[28px]",
        "bg-[#F7F4EC] border border-[#E6DECB]",
      ].join(" ")}
      style={{ boxShadow: "0 18px 50px -24px rgba(43,42,40,0.35)" }}
    >
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 bg-[#2B2A28] flex-shrink-0">
        <div className="flex items-center gap-3">
          <DozeLogo size={38} withZ />
          <div className="leading-tight">
            <p className="doze-serif text-[#F4F1E6] text-[19px] font-semibold tracking-tight">
              {ASSISTANT_NAME}
            </p>
            <p className="text-[#F4F1E6]/55 text-xs">Mattress Crafters Sleep Advisor</p>
          </div>
        </div>

        <button
          type="button"
          onClick={reset}
          aria-label="Start a new conversation"
          title="New conversation"
          className="text-[#F4F1E6]/60 hover:text-[#F4F1E6] hover:bg-white/10 transition-colors p-2 rounded-full"
        >
          <NewChatIcon className="w-[18px] h-[18px]" />
        </button>
      </header>

      {/* Message list */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6 bg-[#F7F4EC] custom-scrollbar">
        <div
          className={[
            "mx-auto w-full max-w-[760px]",
            isIntro ? "min-h-full flex flex-col justify-center" : "",
          ].join(" ")}
        >
          {messages.map((msg, index) => {
            const isLast = index === messages.length - 1;
            return (
              <div key={msg.id} className="doze-msg-in">
                <MessageBubble message={msg} />
                {isLast && isLastMessageStreaming && (
                  <div className="flex justify-start -mt-1 mb-3">
                    <div className="ml-11">
                      <StreamingCursor />
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Suggestion chips on the intro screen */}
          {isIntro && (
            <div className="mt-5 flex flex-wrap justify-center gap-2.5 doze-msg-in">
              {SUGGESTIONS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => sendMessage(s.prompt)}
                  className="rounded-full border border-[#E0D7C4] bg-white px-4 py-2 text-[13px] text-[#2B2A28]/75 hover:border-[#D2785A] hover:text-[#2B2A28] transition-colors"
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Cute thinking indicator */}
          {showThinking && (
            <div className="flex items-end gap-2.5 mb-4 doze-msg-in">
              <DozeLogo size={30} floating={false} className="mb-0.5" />
              <div className="rounded-2xl rounded-tl-md bg-white border border-[#ECE5D6] px-4 py-3">
                <span className="doze-typing">
                  <span />
                  <span />
                  <span />
                </span>
              </div>
            </div>
          )}

          {toolActivity && !showThinking && (
            <div className="flex justify-start mb-2 ml-11">
              <span className="text-xs text-[#2B2A28]/45 italic">{toolActivity}</span>
            </div>
          )}
        </div>
      </div>

      {/* Input area */}
      <div className="flex-shrink-0 px-4 md:px-8 pt-3 pb-4 bg-[#F1ECE0] border-t border-[#E6DECB]">
        <div className="mx-auto w-full max-w-[760px]">
          <form onSubmit={handleSubmit}>
            <PromptBox
              ref={inputRef}
              name="message"
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
              placeholder={
                isStreaming ? "Doze is replying..." : "Ask me about sleep or mattresses..."
              }
              className="w-full"
            />
          </form>
          <p className="text-center text-[11px] text-[#2B2A28]/35 mt-2.5 select-none">
            {ASSISTANT_NAME} can make mistakes. Always check product details on the product page.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Minimal "new chat" icon */
function NewChatIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <line x1="12" y1="8" x2="12" y2="14" />
      <line x1="9" y1="11" x2="15" y2="11" />
    </svg>
  );
}
